import { __assign } from "tslib";
import { assertNumber, assertString } from "../helpers/assertFunctions";
import { FeatureNotSupportException } from "../exception/Exception";
var LocalStorage = /** @class */ (function () {
    function LocalStorage(namespace, expiredDays) {
        if (namespace === void 0) { namespace = "jdw-sdk"; }
        if (expiredDays === void 0) { expiredDays = 0; }
        this.namespace = namespace;
        this.expiredDays = expiredDays;
        if (!window.localStorage) {
            throw new FeatureNotSupportException("localStorage");
        }
    }
    Object.defineProperty(LocalStorage.prototype, "rawKeys", {
        /**
         * 获取所有的原生存储键名
         */
        get: function () {
            return Object.keys(window.localStorage) || [];
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(LocalStorage.prototype, "keys", {
        /**
         * 获取所有的存储键名
         */
        get: function () {
            var _this = this;
            return this.rawKeys
                .filter(function (k) { return k.startsWith(_this.namespace); })
                .map(function (k) { return k.replace(_this.namespace + ".", ""); });
        },
        enumerable: false,
        configurable: true
    });
    LocalStorage.prototype.getKey = function (k) {
        assertString(k);
        return this.namespace + "." + k;
    };
    /**
     * 按命名空间获取所有的存储键名
     * @param namespace
     */
    LocalStorage.prototype.getKeysByNS = function (namespace) {
        assertString(namespace);
        // 避免误传入空串删除全部数据
        if (namespace === "") {
            console.warn("Namespace is empty.");
            return [];
        }
        else {
            // 显式的传入 * 表示删除所有
            if (namespace === "*")
                namespace = "";
            return this.keys.filter(function (k) { return k.startsWith(namespace); });
        }
    };
    // 原生操作方法
    LocalStorage.prototype.getRaw = function (key) {
        return window.localStorage.getItem(key);
    };
    LocalStorage.prototype.setRaw = function (key, value) {
        return window.localStorage.setItem(key, value);
    };
    LocalStorage.prototype.delRaw = function (key) {
        if (!this.rawKeys.includes(key)) {
            return console.warn("Key [" + key + "] does not exists.");
        }
        return window.localStorage.removeItem(key);
    };
    // 包装操作方法
    /**
     * 获取一个值
     * @param key 键
     */
    LocalStorage.prototype.get = function (key) {
        var keyName = this.getKey(key);
        var value = this.getRaw(keyName);
        if (typeof value === "string") {
            if (value === "")
                return value;
            try {
                var result = JSON.parse(value);
                var expireTS = result.expired;
                var now = new Date().getTime();
                // 失效
                if (expireTS > 0 && now > expireTS) {
                    this.delRaw(keyName);
                    console.warn(keyName + " \u5DF2\u8FC7\u671F\u3002");
                    return null;
                }
                if (result.value)
                    return result.value;
                console.warn("\u4F3C\u4E4E\u6CA1\u6709\u8BFB\u5230 " + keyName + " \u4EFB\u4F55\u5185\u5BB9\u3002");
            }
            catch (e) {
                console.warn("LocalStorage.get 方法只能读取由 set 方法写入的值。原生读请用 getRaw() ");
                console.error(e);
            }
            return null;
        }
        return null;
    };
    /**
     * 获取 **当前实例** 中所有值
     */
    LocalStorage.prototype.getAll = function () {
        return this.getByNS("*");
    };
    /**
     * 按命名空间批量查询
     * @param namespace 写入时键名的命名空间（.号分割）
     */
    LocalStorage.prototype.getByNS = function (namespace) {
        var _this = this;
        var keys = this.getKeysByNS(namespace);
        return keys.reduce(function (prev, curr) {
            var _a;
            return __assign(__assign({}, prev), (_a = {}, _a[curr] = _this.get(curr), _a));
        }, {});
    };
    /**
     * 设置单个值
     * @param key 键
     * @param value 值
     * @param expiredDays 过期时间（天数）
     * example:
     *   .set("key", "value", 1 * Time.Day)
     */
    LocalStorage.prototype.set = function (key, value, expiredDays) {
        if (expiredDays === void 0) { expiredDays = 0; }
        var keyName = this.getKey(key);
        var nowTS = new Date().getTime();
        var expiredTS = this.expiredDays || expiredDays;
        if (expiredTS !== 0) {
            assertNumber(expiredTS);
        }
        if (key === "")
            return console.error("Key is empty");
        if (!/^(\w+\.?)+$/.test(key)) {
            return console.error("Key 名只能是 . 号分割的字符。");
        }
        var expired = expiredTS <= 0 ? 0 : nowTS + expiredTS * 24 * 60 * 60 * 1000;
        return this.setRaw(keyName, JSON.stringify({ value: value, expired: expired }));
    };
    /**
     * 删除单个值
     * @param key 键
     */
    LocalStorage.prototype.del = function (key) {
        if (key === "")
            return console.error("Key is empty");
        return this.delRaw(this.getKey(key));
    };
    /**
     * 删除所有值
     */
    LocalStorage.prototype.delAll = function () {
        return this.delByNS("*");
    };
    /**
     * 按命名空间删除值
     * @param namespace 命名空间
     */
    LocalStorage.prototype.delByNS = function (namespace) {
        var _this = this;
        return this.getKeysByNS(namespace).map(function (k) { return _this.del(k); });
    };
    return LocalStorage;
}());
export { LocalStorage };
